import { Fragment, useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { HelpLink } from "../help/links";
import {
  addSpecification,
  canArchiveContracts,
  canAssignInitiator,
  canManageSpecs,
  canCreateContracts,
  canRegisterContracts,
  CONTRACT_STATUS_LABELS,
  createContract,
  DIRECTION_LABELS,
  fetchContract,
  fetchInitiatorCandidates,
  removeSpecification,
  setContractStatus,
  SPEC_KIND_LABELS,
  updateContract,
  updateSpecification,
  type ContractDetail,
  type ContractDirection,
  type ContractInitiator,
  type ContractInput,
  type ContractStatus,
  type Specification,
  type SpecificationInput,
  type SpecificationKind,
} from "../lib/contracts";
import { fetchCounterparties, type Counterparty } from "../lib/counterparties";
import { formatDate, formatMoney, formatPercent, formatRatePerDay } from "../lib/format";
import { FilesPanel } from "./FilesPanel";
import { ObligationsSection } from "./ObligationsPanel";

/**
 * Карточка договора (E5): все правовые параметры ТЗ 4.2.1 (направление, ставки неустойки
 * раздельно за оплату и поставку, потолок, досудебный порядок и срок ответа, подсудность),
 * контакты контрагента и признак аффилированности, спецификации/ДС, статус действует/архив (В11).
 *
 * Права — подсказка UI (сервер авторитетен, матрица 4.3, правило 5): правку договора видит
 * `contract:register`; спецификации — `obligation:manage` (юрист/админ); архив/возврат —
 * `contract:archive` (юрист/админ). Оформление — токены docs/07 (правило 9); деньги/даты — правило 8.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[13px] text-muted";

function DirectionStamp({ direction }: { direction: ContractDirection }) {
  const cls = direction === "sale" ? "text-accent bg-control-bg" : "text-control-fg bg-control-bg";
  return <span className={`stamp ${cls}`}>{DIRECTION_LABELS[direction]}</span>;
}

function StatusStamp({ status }: { status: ContractStatus }) {
  const cls = status === "active" ? "text-done-fg bg-done-bg" : "text-waiting-fg bg-waiting-bg";
  return <span className={`stamp ${cls}`}>{CONTRACT_STATUS_LABELS[status]}</span>;
}

function AffiliatedStamp({ affiliated }: { affiliated: boolean }) {
  return affiliated ? (
    <span className="stamp text-accent bg-control-bg" title="Внутригрупповой контрагент">
      Аффилирован
    </span>
  ) : (
    <span className="stamp text-control-fg bg-control-bg">Внешний</span>
  );
}

// ── Просмотр карточки (/contracts/:id) ────────────────────────────────────────

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly contract: ContractDetail }
  | { readonly kind: "error"; readonly message: string };

export function ContractCardPage() {
  const { id = "" } = useParams();
  // Обязательство, ради которого карточку открыли: переход из сводки, реестра или уведомления
  // (`?obl=`). Отдельного экрана обязательства нет — вместо него подсвечивается строка в карточке.
  const [search] = useSearchParams();
  const highlightId = search.get("obl");
  const { user, logout } = useAuth();
  const roles = user?.roles ?? [];
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchContract(id);
    if (res.ok) {
      setState({ kind: "ready", contract: res.value });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [id, logout]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return (
      <p className="text-muted" role="status">
        Загрузка карточки…
      </p>
    );
  }
  if (state.kind === "error") {
    return (
      <div role="alert" className="rounded-[var(--radius-l)] border border-border bg-surface p-5">
        <p className="text-overdue-fg">{state.message}</p>
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={() => void load()} className={btnPrimary}>
            Повторить
          </button>
          <Link to="/contracts" className={btnSecondary}>
            К реестру
          </Link>
        </div>
      </div>
    );
  }

  const c = state.contract;

  if (editing) {
    return (
      <ContractForm
        mode="edit"
        initial={c}
        onSaved={() => {
          setEditing(false);
          void load();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <section aria-labelledby="contract-title">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Link to="/contracts" className="text-[13px] text-muted hover:text-ink">
          ← Реестр договоров
        </Link>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 id="contract-title" className="num text-[20px] font-bold text-ink">
          Договор № {c.number}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <StatusStamp status={c.status} />
          {canRegisterContracts(roles) && (
            <button type="button" onClick={() => setEditing(true)} className={btnSecondary}>
              Изменить
            </button>
          )}
        </div>
      </div>

      {/*
        ЗОНА РЕКВИЗИТОВ — ТРИ КОЛОНКИ (CR-5, пакет 3; docs/07 §4, уточнено docs/29 §4.2).
        Слева правовые параметры, посередине контрагент, справа файлы и статус договора.

        ПОЧЕМУ НЕ «МАСТЕР-ДЕТАЛЬ» (липкая колонка реквизитов слева, работа справа), как
        говорила прежняя редакция docs/07 §4: липкая колонка на 480 точек плюс зазор оставила
        бы таблице обязательств 1096 — меньше, чем было до всего CR-5. Реквизиты читают один
        раз при открытии карточки, а обязательства обрабатывают; ширину отдаём второму.

        Что это убирает: блоки «Статус договора» и «Файлы» перестают быть полосами во всю
        ширину под одну кнопку — именно они и давали карточке лишнюю высоту (2549 точек).
      */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <ContractParams contract={c} />
        <CounterpartyPanel contract={c} />
        {/* Третья колонка: файлы, под ними статус. Статус виден не всем ролям (архивирование —
            право отдельное), и колонка обязана пережить его отсутствие: тогда в ней остаются
            одни файлы, а сетка не ломается. */}
        <div className="grid content-start gap-4">
          {/* Файлы договора в целом (E6): сканы/вложения; несколько файлов, замена с историей версий. */}
          <section aria-label="Файлы договора">
            <FilesPanel
              owner={{ kind: "contract", id: c.id }}
              canUpload={canRegisterContracts(roles)}
              title="Файлы договора"
            />
          </section>
          <InitiatorPanel contract={c} canAssign={canAssignInitiator(roles)} onChanged={load} />
          {canArchiveContracts(roles) && <ArchivePanel contract={c} onChanged={load} flush />}
        </div>
      </div>

      <SpecificationsSection
        contract={c}
        canManage={canManageSpecs(roles)}
        canUploadFiles={canRegisterContracts(roles)}
        onChanged={load}
      />

      {/* Обязательства договора (E7): вычисляемые статусы (Р-2), правило срока, связи-триггеры. */}
      <ObligationsSection
        contractId={c.id}
        specifications={c.specifications}
        roles={roles}
        highlightId={highlightId}
      />
    </section>
  );
}

/** Пара «подпись — значение» карточки. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-b border-border-soft py-1.5 last:border-b-0">
      <span className="text-[13px] text-muted">{label}</span>
      <span className="text-right text-[14px] text-ink">{children}</span>
    </div>
  );
}

/** Форма записи потолка в договоре: не задан, сумма, процент от цены договора/суммы спецификации. */
type CapForm = "none" | "money" | "contract" | "spec";

function capFormOf(c: ContractDetail | undefined): CapForm {
  if (c?.penCap) return "money";
  if (c?.penCapPercent && c.penCapBase) return c.penCapBase;
  return "none";
}

function capValueOf(c: ContractDetail | undefined): string {
  if (c?.penCap) return c.penCap;
  return c?.penCapPercent ?? "";
}

/**
 * Подпись потолка неустойки: сумма, процент от цены договора или процент от суммы спецификации
 * (CR-2а). Рублёвая величина процентного потолка ЗДЕСЬ НЕ СЧИТАЕТСЯ — она зависит от того, к какой
 * спецификации привязано обязательство, и приходит уже посчитанной в расчёте санкций (правило 2:
 * деньги считает сервер целыми копейками, а не браузер).
 */
function capLabel(c: ContractDetail): string {
  if (c.penCap) return formatMoney(c.penCap);
  if (!c.penCapPercent || !c.penCapBase) return "без потолка";
  const base = c.penCapBase === "contract" ? "цены договора" : "суммы спецификации";
  return `${formatPercent(c.penCapPercent)} от ${base}`;
}

function ContractParams({ contract: c }: { contract: ContractDetail }) {
  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <h2 className="mb-2 text-[15px] font-bold text-ink">Правовые параметры</h2>
      <Row label="Направление">
        <DirectionStamp direction={c.direction} />
      </Row>
      <Row label="Дата договора">
        <span className="num">{formatDate(c.date)}</span>
      </Row>
      {/* E17: номер по внутреннему Реестру договоров заказчика. Нумерацию ведёт заказчик ВНЕ
          Системы, поэтому поле необязательное и без требования уникальности — наш собственный
          номер договора стоит в заголовке карточки и уникален. */}
      <Row label="Регистрационный номер по Реестру договоров">
        <span className="num">{c.registryNumber ?? "—"}</span>
      </Row>
      <Row label="Предмет">{c.subject ?? "—"}</Row>
      <Row label="Срок действия">{c.validityTerm ?? "—"}</Row>
      <Row label="Неустойка за просрочку оплаты">
        <span className="num">{formatRatePerDay(c.penPayRate)}</span>
      </Row>
      <Row label="Неустойка за просрочку поставки">
        <span className="num">{formatRatePerDay(c.penDelRate)}</span>
      </Row>
      <Row label="Цена договора">
        <span className="num">{c.price ? formatMoney(c.price) : "—"}</span>
      </Row>
      <Row label="Договорный потолок неустойки">
        <span className="num">{capLabel(c)}</span>
      </Row>
      <Row label="Досудебный (претензионный) порядок">
        {c.claimRequired ? "обязателен" : "не обязателен"}
      </Row>
      <Row label="Срок ответа на претензию">{c.claimDays == null ? "—" : `${c.claimDays} дн.`}</Row>
      <Row label="Подсудность">{c.court ?? "—"}</Row>
    </div>
  );
}

function CounterpartyPanel({ contract: c }: { contract: ContractDetail }) {
  const cp = c.counterparty;
  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-bold text-ink">Контрагент</h2>
        <AffiliatedStamp affiliated={cp.affiliated} />
      </div>
      <div className="text-[14px] text-ink">{cp.name}</div>
      <div className="num mt-1 text-[12px] text-muted">
        ИНН {cp.inn ?? "—"}
        {cp.kpp && <> · КПП {cp.kpp}</>}
        {cp.ogrn && <> · ОГРН {cp.ogrn}</>}
      </div>
      {cp.address && <div className="mt-1 text-[12px] text-muted">{cp.address}</div>}

      <h3 className="mt-3 mb-1 text-[13px] font-bold text-ink">Контактные лица</h3>
      {cp.contacts.length === 0 ? (
        <p className="text-[13px] text-muted">Контактные лица не указаны.</p>
      ) : (
        <ul className="space-y-1">
          {cp.contacts.map((ct, i) => (
            <li key={i} className="text-[13px] text-ink">
              {ct.fio}
              {ct.position && <span className="text-muted"> · {ct.position}</span>}
              {ct.phone && <span className="num text-muted"> · {ct.phone}</span>}
              {ct.email && <span className="text-muted"> · {ct.email}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[12px] text-muted">
        Реквизиты и контакты ведутся в разделе «Контрагенты».
      </p>
    </div>
  );
}

/** Панель архивирования (В11): доступна юристу/админу; при живых обязательствах — отказ с причиной. */
/**
 * ИНИЦИАТОР ДОГОВОРА (E17, ответ заказчика В36 от 16.09.2026).
 *
 * Панель видят ВСЕ роли — кто ведёт договор, должно быть известно каждому, кто его открыл.
 * Переназначать вправе юрист и администратор; у остальных ролей панель остаётся справкой.
 * Отсутствие выпадающего списка — не запрет: отказ даёт сервер (правило 5), и он же решает
 * окончательно.
 */
function InitiatorPanel({
  contract: c,
  canAssign,
  onChanged,
}: {
  contract: ContractDetail;
  canAssign: boolean;
  onChanged: () => Promise<void>;
}) {
  const [candidates, setCandidates] = useState<ContractInitiator[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Отсутствие поля читаем как «не назначен», а не как отказ отрисовать карточку: панель стои́т
  // посреди договора, и уронить из-за неё весь экран — несоразмерная плата за строку имени.
  const initiator = c.initiator ?? null;

  // Список кандидатов грузится ТОЛЬКО тому, кто вправе назначать: остальным он не нужен, а
  // маршрут ему всё равно откажет.
  useEffect(() => {
    if (!canAssign) return;
    void fetchInitiatorCandidates().then((res) => {
      if (res.ok) setCandidates(res.value);
    });
  }, [canAssign]);

  const assign = async (value: string) => {
    setBusy(true);
    setError(null);
    // Пустое значение = «снять назначение»: договор возвращается в состояние «не назначен»,
    // законное и видимое отбором реестра.
    const res = await updateContract(c.id, { initiatorId: value === "" ? null : value });
    setBusy(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <h2 className="mb-1 text-[15px] font-bold text-ink">Инициатор договора</h2>
      {initiator === null ? (
        <p className="text-[13px] text-muted">
          Инициатор не назначен. Предупреждения по срокам идут ответственной роли, а не лично.
        </p>
      ) : (
        <p className="text-[14px] text-ink">
          {initiator.fullName}
          {!initiator.isActive && (
            <span className="ml-1.5 text-[12px] text-muted">(учётка отключена)</span>
          )}
        </p>
      )}

      {canAssign && (
        <div className="mt-3">
          <label htmlFor="contract-initiator" className={labelCls}>
            Кому поручен договор
          </label>
          <select
            id="contract-initiator"
            className={inputCls}
            disabled={busy || candidates === null}
            value={initiator?.id ?? ""}
            onChange={(e) => void assign(e.target.value)}
          >
            <option value="">— не назначен —</option>
            {/* Деактивированный Инициатор в списке кандидатов не приходит, но в договоре может
                стоять: добавляем его отдельной строкой, иначе выпадающий список показал бы
                «не назначен» у договора, где ответственный есть. */}
            {initiator !== null && !initiator.isActive && (
              <option value={initiator.id}>{initiator.fullName} (учётка отключена)</option>
            )}
            {(candidates ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[12px] text-muted">
            Смена Инициатора записывается в журнал изменений и переводит на него личные
            предупреждения по срокам этого договора.
          </p>
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
    </div>
  );
}

function ArchivePanel({
  contract,
  onChanged,
  flush = false,
}: {
  contract: ContractDetail;
  onChanged: () => Promise<void>;
  /** Без верхнего отступа: панель стои́т в колонке реквизитов, где расстояния задаёт сетка. */
  flush?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  /**
   * Не только текст отказа, но и ЕГО ПРИРОДА. Ссылка «Подробнее» ставится ТОЛЬКО у доменного
   * отказа — «в архив нельзя, есть незакрытые обязательства» (409). Общий отказ в правах (403)
   * в E16 не трогается: он молчалив намеренно, и приделывать к нему объяснение — работа про
   * права, а не про документацию (граница записана в плане E16 §3).
   */
  const [error, setError] = useState<{ text: string; domain: boolean } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const toArchive = contract.status === "active";

  const apply = async () => {
    setBusy(true);
    setError(null);
    const res = await setContractStatus(contract.id, toArchive ? "archived" : "active");
    setBusy(false);
    setConfirm(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError({ text: res.message, domain: res.reason === "conflict" });
  };

  return (
    <div
      className={`rounded-[var(--radius-l)] border border-border bg-surface p-4 ${
        flush ? "" : "mt-4"
      }`}
    >
      <h2 className="mb-1 text-[15px] font-bold text-ink">Статус договора</h2>
      <p className="text-[13px] text-muted">
        {toArchive
          ? "Перевод в архив снимает договор с активного контроля. Доступен, когда закрыты все обязательства."
          : "Договор в архиве. Возврат вернёт его на активный контроль."}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {confirm ? (
          <>
            <span className="text-[13px] text-ink">
              {toArchive ? "Перевести договор в архив?" : "Вернуть договор из архива?"}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply()}
              className={btnPrimary}
            >
              Подтвердить
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm(false)}
              className={btnSecondary}
            >
              Отмена
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setConfirm(true)} className={btnSecondary}>
            {toArchive ? "Перевести в архив" : "Вернуть из архива"}
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error.text} {error.domain && <HelpLink topic="contractArchiveBlocked" />}
        </p>
      )}
    </div>
  );
}

// ── Спецификации/ДС ───────────────────────────────────────────────────────────

function SpecificationsSection({
  contract,
  canManage,
  canUploadFiles,
  onChanged,
}: {
  contract: ContractDetail;
  canManage: boolean;
  /** Может ли роль загружать файлы к спецификации (право `contract:register`, отдельно от `canManage`). */
  canUploadFiles: boolean;
  onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filesOpenId, setFilesOpenId] = useState<string | null>(null);
  // Число колонок таблицы (для colSpan строки с файлами): базовые 4 + столбец действий у canManage.
  const colCount = canManage ? 5 : 4;

  return (
    <section aria-labelledby="specs-title" className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="specs-title" className="text-[15px] font-bold text-ink">
          Спецификации, приложения и доп. соглашения
        </h2>
        {canManage && !adding && (
          <button type="button" onClick={() => setAdding(true)} className={btnPrimary}>
            Добавить
          </button>
        )}
      </div>

      {canManage && adding && (
        <div className="mt-3">
          <SpecForm
            contractId={contract.id}
            onSaved={() => {
              setAdding(false);
              void onChanged();
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      <div className="mt-3">
        {contract.specifications.length === 0 ? (
          <p className="text-[13px] text-muted">
            Спецификаций пока нет.{" "}
            {canManage
              ? "Добавьте спецификацию, приложение или доп. соглашение."
              : "Их ведут юрист и администратор."}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-l)] border border-border bg-surface">
            <table className="w-full border-collapse text-[14px]">
              <caption className="sr-only">Спецификации договора</caption>
              <thead>
                <tr className="border-b border-border text-left text-[12px] text-muted">
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Вид / №
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Дата
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Сумма
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Предмет
                  </th>
                  {canManage && (
                    <th scope="col" className="px-4 py-2.5 font-bold">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {contract.specifications.map((s) =>
                  editingId === s.id ? (
                    <tr key={s.id} className="border-b border-border-soft last:border-b-0">
                      <td colSpan={colCount} className="px-4 py-3">
                        <SpecForm
                          contractId={contract.id}
                          initial={s}
                          onSaved={() => {
                            setEditingId(null);
                            void onChanged();
                          }}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  ) : (
                    <Fragment key={s.id}>
                      <SpecRow
                        spec={s}
                        canManage={canManage}
                        filesOpen={filesOpenId === s.id}
                        onToggleFiles={() =>
                          setFilesOpenId((prev) => (prev === s.id ? null : s.id))
                        }
                        onEdit={() => setEditingId(s.id)}
                        onChanged={onChanged}
                      />
                      {filesOpenId === s.id && (
                        <tr className="border-b border-border-soft last:border-b-0">
                          <td colSpan={colCount} className="bg-bg px-4 py-3">
                            <FilesPanel
                              owner={{ kind: "specification", id: s.id }}
                              canUpload={canUploadFiles}
                              title={`Файлы спецификации № ${s.number}`}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function SpecRow({
  spec,
  canManage,
  filesOpen,
  onToggleFiles,
  onEdit,
  onChanged,
}: {
  spec: Specification;
  canManage: boolean;
  filesOpen: boolean;
  onToggleFiles: () => void;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    const res = await removeSpecification(spec.id);
    setBusy(false);
    setConfirm(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <tr className="border-b border-border-soft align-top last:border-b-0">
      <td className="px-4 py-3">
        <div className="text-ink">{SPEC_KIND_LABELS[spec.kind]}</div>
        <div className="num text-[12px] text-muted">№ {spec.number}</div>
        {/* Файлы спецификации (E6) — доступ на просмотр/скачивание всем ролям; кнопка видна всегда. */}
        <button
          type="button"
          className="mt-1 text-[12px] text-accent hover:underline"
          aria-expanded={filesOpen}
          onClick={onToggleFiles}
        >
          {filesOpen ? "Скрыть файлы" : "Файлы"}
        </button>
      </td>
      <td className="num px-4 py-3 text-ink">{formatDate(spec.date)}</td>
      <td className="num px-4 py-3 text-ink">{formatMoney(spec.amount)}</td>
      <td className="px-4 py-3 text-muted">{spec.subject ?? "—"}</td>
      {canManage && (
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {confirm ? (
              <>
                <span className="text-[13px] text-ink">Удалить?</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void doDelete()}
                  className={btnPrimary}
                >
                  Да
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirm(false)}
                  className={btnSecondary}
                >
                  Нет
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onEdit} className={btnSecondary}>
                  Изменить
                </button>
                <button type="button" onClick={() => setConfirm(true)} className={btnSecondary}>
                  Удалить
                </button>
              </>
            )}
          </div>
          {error && (
            <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
              {error}
            </p>
          )}
        </td>
      )}
    </tr>
  );
}

function SpecForm({
  contractId,
  initial,
  onSaved,
  onCancel,
}: {
  contractId: string;
  initial?: Specification;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<SpecificationKind>(initial?.kind ?? "specification");
  const [number, setNumber] = useState(initial?.number ?? "");
  const [date, setDate] = useState(initial?.date ?? "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const input: SpecificationInput = {
      kind,
      number: number.trim(),
      date,
      amount: amount.trim(),
      subject: subject.trim(),
    };
    const res = initial
      ? await updateSpecification(initial.id, input)
      : await addSpecification(contractId, input);
    setBusy(false);
    if (res.ok) {
      onSaved();
      return;
    }
    setError(res.message);
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[var(--radius-m)] border border-border bg-bg p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="sp-kind" className={labelCls}>
            Вид
          </label>
          <select
            id="sp-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as SpecificationKind)}
            className={inputCls}
          >
            <option value="specification">{SPEC_KIND_LABELS.specification}</option>
            <option value="annex">{SPEC_KIND_LABELS.annex}</option>
            <option value="agreement">{SPEC_KIND_LABELS.agreement}</option>
          </select>
        </div>
        <div>
          <label htmlFor="sp-number" className={labelCls}>
            Номер
          </label>
          <input
            id="sp-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="sp-date" className={labelCls}>
            Дата
          </label>
          <input
            id="sp-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="sp-amount" className={labelCls}>
            Сумма, ₽
          </label>
          <input
            id="sp-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="—"
            className={`num ${inputCls}`}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="sp-subject" className={labelCls}>
            Предмет
          </label>
          <input
            id="sp-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Сохранение…" : initial ? "Сохранить" : "Добавить"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}

// ── Создание договора (/contracts/new) ────────────────────────────────────────

export function ContractCreatePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ready"; counterparties: Counterparty[] }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  // Заводить договор без права нельзя (сервер вернёт 403) — уводим на реестр заранее.
  // Право именно СОЗДАНИЯ (CR-3): оно у всех шести ролей, в отличие от правки реквизитов.
  useEffect(() => {
    if (user && !canCreateContracts(user.roles)) navigate("/contracts", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchCounterparties();
    if (res.ok) {
      setState({ kind: "ready", counterparties: res.value });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [logout]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section aria-labelledby="new-contract-title">
      <div className="mb-1">
        <Link to="/contracts" className="text-[13px] text-muted hover:text-ink">
          ← Реестр договоров
        </Link>
      </div>
      <h1 id="new-contract-title" className="text-[20px] font-bold text-ink">
        Новый договор
      </h1>

      {state.kind === "loading" && (
        <p className="mt-4 text-muted" role="status">
          Загрузка…
        </p>
      )}
      {state.kind === "error" && (
        <div
          role="alert"
          className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-5"
        >
          <p className="text-overdue-fg">{state.message}</p>
          <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
            Повторить
          </button>
        </div>
      )}
      {state.kind === "ready" && (
        <div className="mt-4">
          {state.counterparties.length === 0 ? (
            <div className="rounded-[var(--radius-l)] border border-border bg-surface p-5 text-muted">
              <p className="max-w-[var(--w-text)]">
                Сначала заведите контрагента в разделе «Контрагенты» — договор заключается с
                контрагентом.
              </p>
            </div>
          ) : (
            <ContractForm
              mode="create"
              counterparties={state.counterparties}
              onSaved={(contract) => navigate(`/contracts/${contract.id}`)}
              onCancel={() => navigate("/contracts")}
            />
          )}
        </div>
      )}
    </section>
  );
}

// ── Общая форма договора (создание/правка) ────────────────────────────────────

function ContractForm({
  mode,
  initial,
  counterparties,
  onSaved,
  onCancel,
}: {
  mode: "create" | "edit";
  initial?: ContractDetail;
  counterparties?: Counterparty[];
  onSaved: (contract: ContractDetail) => void;
  onCancel: () => void;
}) {
  const [counterpartyId, setCounterpartyId] = useState(
    initial?.counterparty.id ?? counterparties?.[0]?.id ?? "",
  );
  const [number, setNumber] = useState(initial?.number ?? "");
  const [date, setDate] = useState(initial?.date ?? "");
  const [direction, setDirection] = useState<ContractDirection>(initial?.direction ?? "sale");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [validityTerm, setValidityTerm] = useState(initial?.validityTerm ?? "");
  const [penPayRate, setPenPayRate] = useState(initial?.penPayRate ?? "");
  const [penDelRate, setPenDelRate] = useState(initial?.penDelRate ?? "");
  // Потолок неустойки — ОДНО поле ввода и переключатель формы (CR-2а): в договоре он записан
  // либо суммой, либо процентом, и держать два независимых поля значило бы позволить ввести оба
  // сразу — состояние, которое сервер и СУБД всё равно отвергнут.
  const [capForm, setCapForm] = useState<CapForm>(capFormOf(initial));
  const [capValue, setCapValue] = useState(capValueOf(initial));
  const [price, setPrice] = useState(initial?.price ?? "");
  const [claimRequired, setClaimRequired] = useState(initial?.claimRequired ?? false);
  const [claimDays, setClaimDays] = useState(
    initial?.claimDays == null ? "" : String(initial.claimDays),
  );
  const [court, setCourt] = useState(initial?.court ?? "");
  /** E17: номер по внутреннему Реестру заказчика. Пустая строка = «стереть» (сервер → null). */
  const [registryNumber, setRegistryNumber] = useState(initial?.registryNumber ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const base: ContractInput = {
      number: number.trim(),
      date,
      direction,
      subject: subject.trim(),
      validityTerm: validityTerm.trim(),
      penPayRate: penPayRate.trim(),
      penDelRate: penDelRate.trim(),
      // Шлём ВСЕ три поля при каждом сохранении: переключение формы обязано стереть прежнюю.
      penCap: capForm === "money" ? capValue.trim() : "",
      penCapPercent: capForm === "contract" || capForm === "spec" ? capValue.trim() : "",
      penCapBase: capForm === "contract" || capForm === "spec" ? capForm : "",
      price: price.trim(),
      claimRequired,
      claimDays: claimDays.trim() === "" ? null : Number(claimDays),
      court: court.trim(),
      registryNumber: registryNumber.trim(),
    };
    // Контрагент задаётся при создании; при правке остаётся прежним (переназначение — редкий случай,
    // ведётся отдельно). Направление/номер/дата и правовые параметры правятся всегда.
    const input: ContractInput = mode === "create" ? { ...base, counterpartyId } : base;
    const res = initial ? await updateContract(initial.id, input) : await createContract(input);
    setBusy(false);
    if (res.ok) {
      onSaved(res.value);
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={onSubmit}
      aria-labelledby="contract-form-title"
      /* Форма — в колонке --w-form (поля по два в ряд): на рабочей области 1600 px поле под
         номер договора «07/23» растянулось бы на 800 px и провоцировало ввод лишнего. */
      className="max-w-[var(--w-form)] rounded-[var(--radius-l)] border border-border bg-surface p-4"
    >
      <h2 id="contract-form-title" className="mb-3 text-[15px] font-bold text-ink">
        {mode === "create" ? "Новый договор" : `Изменение договора № ${initial?.number ?? ""}`}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {mode === "create" && (
          <div className="sm:col-span-2">
            <label htmlFor="c-cp" className={labelCls}>
              Контрагент
            </label>
            <select
              id="c-cp"
              value={counterpartyId}
              onChange={(e) => setCounterpartyId(e.target.value)}
              className={inputCls}
              required
            >
              {counterparties?.map((cp) => (
                <option key={cp.id} value={cp.id}>
                  {cp.name}
                  {cp.inn ? ` (ИНН ${cp.inn})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="c-number" className={labelCls}>
            Номер договора
          </label>
          <input
            id="c-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          <label htmlFor="c-date" className={labelCls}>
            Дата договора
          </label>
          <input
            id="c-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`num ${inputCls}`}
            required
          />
        </div>
        <div>
          {/* E17 (ответ заказчика В40): номер, под которым договор учтён во внутреннем Реестре
              договоров заказчика. Нумерация ведётся ВНЕ Системы, поэтому поле необязательное и
              совпадения не отвергаются — останавливать регистрацию из-за чужой нумерации мы не
              вправе. */}
          <label htmlFor="c-registry-number" className={labelCls}>
            Регистрационный номер по Реестру договоров (необязательно)
          </label>
          <input
            id="c-registry-number"
            value={registryNumber}
            onChange={(e) => setRegistryNumber(e.target.value)}
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="c-direction" className={labelCls}>
            Направление
          </label>
          <select
            id="c-direction"
            value={direction}
            onChange={(e) => setDirection(e.target.value as ContractDirection)}
            className={inputCls}
          >
            <option value="sale">{DIRECTION_LABELS.sale}</option>
            <option value="purchase">{DIRECTION_LABELS.purchase}</option>
          </select>
        </div>
        <div>
          <label htmlFor="c-validity" className={labelCls}>
            Срок действия
          </label>
          <input
            id="c-validity"
            value={validityTerm}
            onChange={(e) => setValidityTerm(e.target.value)}
            placeholder="напр. до полного исполнения обязательств"
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="c-subject" className={labelCls}>
            Предмет
          </label>
          <input
            id="c-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="c-penpay" className={labelCls}>
            Неустойка за просрочку оплаты, % в день
          </label>
          <input
            id="c-penpay"
            value={penPayRate}
            onChange={(e) => setPenPayRate(e.target.value)}
            inputMode="decimal"
            placeholder="напр. 0,1"
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="c-pendel" className={labelCls}>
            Неустойка за просрочку поставки, % в день
          </label>
          <input
            id="c-pendel"
            value={penDelRate}
            onChange={(e) => setPenDelRate(e.target.value)}
            inputMode="decimal"
            placeholder="напр. 0,05"
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="c-price" className={labelCls}>
            Цена договора, ₽
          </label>
          <input
            id="c-price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="если указана в договоре"
            className={`num ${inputCls}`}
            aria-describedby="c-price-hint"
          />
          {/* Ответ юриста на В31 (13.08.2026): процент от суммы договора применяется к тем
              договорам, которые не являются рамочными. Пустое поле у рамочного — норма, а не
              недозаполнение: потолок «процентом от цены договора» тогда просто не вычисляется
              (Р-42), и расчёт говорит об этом прямо. Без подсказки юрист искал бы цену там,
              где её нет. */}
          <p id="c-price-hint" className="mt-1 text-[12px] text-muted">
            У рамочных договоров не заполняется.
          </p>
        </div>
        <div>
          <label htmlFor="c-capform" className={labelCls}>
            Потолок неустойки задан
          </label>
          <select
            id="c-capform"
            value={capForm}
            onChange={(e) => setCapForm(e.target.value as CapForm)}
            className={inputCls}
          >
            <option value="none">без потолка</option>
            <option value="money">суммой в рублях</option>
            <option value="contract">процентом от цены договора</option>
            <option value="spec">процентом от суммы спецификации</option>
          </select>
        </div>
        {capForm !== "none" && (
          <div>
            <label htmlFor="c-pencap" className={labelCls}>
              {capForm === "money" ? "Потолок неустойки, ₽" : "Потолок неустойки, %"}
            </label>
            <input
              id="c-pencap"
              value={capValue}
              onChange={(e) => setCapValue(e.target.value)}
              inputMode="decimal"
              placeholder={capForm === "money" ? "напр. 100000" : "напр. 10"}
              className={`num ${inputCls}`}
            />
          </div>
        )}
        <div>
          <label htmlFor="c-claimdays" className={labelCls}>
            Срок ответа на претензию, дней
          </label>
          <input
            id="c-claimdays"
            type="number"
            min={0}
            value={claimDays}
            onChange={(e) => setClaimDays(e.target.value)}
            className={`num ${inputCls}`}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="c-court" className={labelCls}>
            Подсудность (арбитражная оговорка)
          </label>
          <input
            id="c-court"
            value={court}
            onChange={(e) => setCourt(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <label className="mt-3 inline-flex items-center gap-2 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={claimRequired}
          onChange={(e) => setClaimRequired(e.target.checked)}
          className="accent-accent"
        />
        Обязателен досудебный (претензионный) порядок (ч. 5 ст. 4 АПК РФ)
      </label>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Сохранение…" : mode === "create" ? "Зарегистрировать" : "Сохранить"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}
